/**
 * Case Studies Filter
 * Handles filtering of case study cards based on service and industry tags.
 * Supports multiple selections per filter; selected tags appear on the left with X to clear.
 */

const CLEAR_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';

class CaseStudiesFilter {
  constructor() {
    this.activeFilters = {
      service: [],
      industry: []
    };

    this.init();
  }

  init() {
    this.filterButtons = document.querySelectorAll('.case-studies__filter-option');
    this.cards = document.querySelectorAll('.case-study-card:not(.case-study-card--placeholder)');
    this.noResultsMessage = document.querySelector('.case-studies__no-results');
    this.filterDropdowns = document.querySelectorAll('.case-studies__filter-dropdown');

    this.removeDuplicateFilters();

    this.filterButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleFilterClick(e));
    });

    // Delegate clear (X) clicks – tags are created dynamically
    document.querySelectorAll('.case-studies__filters').forEach(container => {
      container.addEventListener('click', (e) => {
        const clearBtn = e.target.closest('.case-studies__filter-selected-clear');
        if (!clearBtn) return;
        e.preventDefault();
        e.stopPropagation();
        const tag = clearBtn.closest('.case-studies__filter-selected-tag');
        const filterType = tag && tag.dataset.filterType;
        const filterValue = tag && tag.dataset.filterValue;
        if (filterType && filterValue) {
          this.removeFilterValue(filterType, filterValue);
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.case-studies__filter-dropdown')) {
        this.filterDropdowns.forEach(dropdown => dropdown.removeAttribute('open'));
      }
    });

    this.renderAllSelectedTags();
    this.applyFilters();
  }

  removeDuplicateFilters() {
    const filterTypes = ['service', 'industry'];
    filterTypes.forEach(type => {
      const options = document.querySelectorAll(
        `.case-studies__filter-option[data-filter-type="${type}"]`
      );
      const seen = new Set();
      options.forEach(option => {
        const value = option.dataset.filterValue;
        if (seen.has(value) && value !== 'all') {
          option.remove();
        } else {
          seen.add(value);
        }
      });
    });
  }

  handleFilterClick(e) {
    const button = e.currentTarget;
    const filterType = button.dataset.filterType;
    const filterValue = button.dataset.filterValue;
    const arr = this.activeFilters[filterType];

    if (filterValue === 'all') {
      this.activeFilters[filterType] = [];
    } else {
      const idx = arr.indexOf(filterValue);
      if (idx === -1) {
        arr.push(filterValue);
      } else {
        arr.splice(idx, 1);
      }
    }

    this.updateButtonActiveStates(filterType);
    this.renderSelectedTags(filterType);
    button.closest('.case-studies__filter-dropdown').removeAttribute('open');
    this.applyFilters();
  }

  removeFilterValue(filterType, filterValue) {
    const arr = this.activeFilters[filterType];
    const idx = arr.indexOf(filterValue);
    if (idx !== -1) {
      arr.splice(idx, 1);
      this.updateButtonActiveStates(filterType);
      this.renderSelectedTags(filterType);
      this.applyFilters();
    }
  }

  updateButtonActiveStates(filterType) {
    const selected = this.activeFilters[filterType];
    const buttons = document.querySelectorAll(
      `.case-studies__filter-option[data-filter-type="${filterType}"]`
    );
    buttons.forEach(btn => {
      const value = btn.dataset.filterValue;
      if (value === 'all') {
        btn.classList.toggle('active', selected.length === 0);
      } else {
        btn.classList.toggle('active', selected.indexOf(value) !== -1);
      }
    });
  }

  getOptionLabel(filterType, filterValue) {
    const btn = document.querySelector(
      `.case-studies__filter-option[data-filter-type="${filterType}"][data-filter-value="${filterValue}"]`
    );
    return btn ? btn.textContent.trim() : filterValue;
  }

  renderSelectedTags(filterType) {
    const container = document.querySelector(
      `.case-studies__filter-selected-tags[data-filter-type="${filterType}"]`
    );
    if (!container) return;

    container.innerHTML = '';
    const values = this.activeFilters[filterType];
    const ariaLabel = filterType === 'service' ? 'Clear service filter' : 'Clear industry filter';

    values.forEach(value => {
      const label = this.getOptionLabel(filterType, value);
      const tag = document.createElement('span');
      tag.className = 'case-studies__filter-selected-tag';
      tag.dataset.filterType = filterType;
      tag.dataset.filterValue = value;
      tag.innerHTML = `<span class="case-studies__filter-selected-label">${escapeHtml(label)}</span><button type="button" class="case-studies__filter-selected-clear" aria-label="${escapeHtml(ariaLabel)}">${CLEAR_SVG}</button>`;
      container.appendChild(tag);
    });
  }

  renderAllSelectedTags() {
    this.renderSelectedTags('service');
    this.renderSelectedTags('industry');
    ['service', 'industry'].forEach(type => this.updateButtonActiveStates(type));
  }

  applyFilters() {
    let visibleCount = 0;
    this.cards.forEach(card => {
      const show = this.shouldShowCard(card);
      card.classList.toggle('hidden', !show);
      if (show) visibleCount++;
    });
    if (this.noResultsMessage) {
      this.noResultsMessage.style.display = visibleCount === 0 ? 'block' : 'none';
    }
  }

  shouldShowCard(card) {
    const serviceMatch = this.matchFilter(card, 'service', this.activeFilters.service);
    const industryMatch = this.matchFilter(card, 'industry', this.activeFilters.industry);
    return serviceMatch && industryMatch;
  }

  matchFilter(card, filterType, selectedValues) {
    if (selectedValues.length === 0) return true;
    const datasetKey = filterType === 'industry' ? 'industries' : `${filterType}s`;
    const cardTags = (card.dataset[datasetKey] || '').trim();
    const normalizedCardTags = cardTags
      .split(',')
      .map(t => this.normalizeString(t.trim()))
      .filter(t => t.length > 0);
    // Cards with no tags for this filter type should be hidden when this filter has selections
    if (normalizedCardTags.length === 0) return false;
    return selectedValues.some(value => {
      const n = this.normalizeString(value);
      return normalizedCardTags.some(tag =>
        tag.includes(n) || n.includes(tag)
      );
    });
  }

  normalizeString(str) {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CaseStudiesFilter());
} else {
  new CaseStudiesFilter();
}
